/**
 * ViewHeader — shared header for all views.
 *
 * Renders: icon + title + filter button + overflow menu (⋮) + count.
 * Extracted from the inline header in ViewShell so it can be shared
 * and extended with per-view actions.
 */

import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import EllipsisIcon from "@/shared/icons/pikaicons/stroke/general/three-dots-menu-horizontal.svg?react"
import { cn } from "@/shared/utils/general/cn"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"
import { useViewContext } from "@/app/stores/viewContext"
import { shortcutLabel } from "@/shared/utils/platform/shortcutLabel"
import { ViewFilterButton } from "./ViewFilterButton"
import { ChoosableTags } from "@/features/tags"
import { useViewMenuActions } from "../hooks/useViewMenuActions"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ViewHeaderAction {
  label: string
  icon?: React.ReactNode
  shortcut?: string
  onClick: () => void
  variant?: "default" | "destructive"
  disabled?: boolean
}

export interface ViewHeaderProps {
  /** View title */
  title: string
  /** Icon type from registry (null = no icon) */
  icon?: RegisteredIconType | null
  /** Total task count */
  totalCount: number
  /** Whether to show tag filter chips below the header */
  showTagFilter?: boolean
  /** Extra actions for the overflow menu (view-specific) */
  actions?: ViewHeaderAction[]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ViewHeader({
  title,
  icon,
  totalCount,
  showTagFilter,
  actions,
}: ViewHeaderProps) {
  const { t } = useTranslation()
  const { value: isColorVariant } = useSettingBoolean("#theme-variant-color")
  const iconMode = isColorVariant ? ("color" as const) : ("monochrome" as const)
  const selectAll = useViewContext((s) => s.selectAll)
  const clearSelection = useViewContext((s) => s.clearSelection)
  const selectedCount = useViewContext((s) => s.selectedTaskIds.length)
  const allSelected = totalCount > 0 && selectedCount >= totalCount
  const viewMenuActions = useViewMenuActions()

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      clearSelection()
    } else {
      selectAll()
    }
  }, [selectAll, clearSelection, allSelected])

  const hasPageActions = actions && actions.length > 0

  return (
    <div className="mb-4 px-3 desk:px-0" data-header>
      <div className="flex items-center gap-2">
        {icon && <StaticIcon type={icon} active size={36} mode={iconMode} />}
        <h1 className="min-w-0 flex-1 text-[28px] font-medium leading-[1.3]">
          {title}
        </h1>
        <ViewFilterButton />

        {/* Overflow menu (⋮) — hidden on mobile where TopNavMenu handles this */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "relative hidden shrink-0 items-center justify-center rounded-lg p-1.5 text-muted-foreground transition-colors hover:text-foreground desk:flex",
              )}
              aria-label={t("More options")}
            >
              <EllipsisIcon className="size-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            {/* Select All / Deselect All */}
            {totalCount > 0 && (
              <DropdownMenuItem onClick={handleSelectAll}>
                {allSelected ? t("Deselect All") : t("Select All")}
                <DropdownMenuShortcut>
                  {shortcutLabel("mod+a")}
                </DropdownMenuShortcut>
              </DropdownMenuItem>
            )}

            {/* Page-specific actions (e.g. Group toggle) */}
            {hasPageActions && totalCount > 0 && <DropdownMenuSeparator />}
            {actions?.map((action) => (
              <DropdownMenuItem
                key={action.label}
                onClick={action.onClick}
                disabled={action.disabled}
                className={cn(
                  action.variant === "destructive" &&
                    "text-destructive focus:text-destructive",
                )}
              >
                {action.icon && (
                  <span className="mr-2 inline-flex size-4 items-center justify-center">
                    {action.icon}
                  </span>
                )}
                {action.label}
                {action.shortcut && (
                  <DropdownMenuShortcut>
                    {shortcutLabel(action.shortcut)}
                  </DropdownMenuShortcut>
                )}
              </DropdownMenuItem>
            ))}

            {/* Built-in view actions (New Project, Paste, Share) */}
            {viewMenuActions.length > 0 && (hasPageActions || totalCount > 0) && (
              <DropdownMenuSeparator />
            )}
            {viewMenuActions.map((action) => (
              <DropdownMenuItem
                key={action.label}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon && (
                  <span className="mr-2 inline-flex size-4 items-center justify-center">
                    {action.icon}
                  </span>
                )}
                {action.label}
                {action.shortcut && (
                  <DropdownMenuShortcut>
                    {shortcutLabel(action.shortcut)}
                  </DropdownMenuShortcut>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

      </div>
      {showTagFilter && <ChoosableTags />}
    </div>
  )
}
